import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { matchPath } from 'react-router';
import styled from 'styled-components';

import { LogoIcon } from 'assets/icons';
import { BREAKPOINTS } from 'constants/common';

const steps = [
  { label: 'Create login', mobileLabel: 'Step 1', path: '/account' },
  { label: 'Choose plan', mobileLabel: 'Step 2', path: '/plan' },
  { label: 'Start trial', mobileLabel: 'Step 3', path: '/payment' },
];

const getActiveIndex = (pathname: string) => {
  return steps.findIndex(step => !!matchPath(pathname, { path: step.path, exact: true }));
};

function StepComponent(props: any) {
  const isActive = props.activeIndex >= props.index;
  const title = isActive ? '' : 'Progress to the next steps by completing the current page.';

  return (
    <Step onClick={() => props.handleClick(props.step.path, isActive)} title={title}>
      <Rectangle active={isActive} />
      <StepText active={isActive}>{props.step.label}</StepText>
      <MobileStepText active={isActive}>{props.step.mobileLabel}</MobileStepText>
    </Step>
  );
}

const Header = () => {
  let history = useHistory();
  const location = useLocation();
  const activeIndex = getActiveIndex(location.pathname);

  const handleClick = (path: string, isActive: boolean) => {
    if (!isActive) return;

    history.push(path);
  };

  return (
    <Wrap>
      <LogoIconStyled />

      <Flex>
        <Steps>
          {steps.map((step, index) => (
            <StepComponent
              key={step.path}
              activeIndex={activeIndex}
              handleClick={handleClick}
              step={step}
              index={index}
            />
          ))}
        </Steps>
      </Flex>
      <div style={{ width: '128px' }} />
    </Wrap>
  );
};

export default Header;

const Wrap = styled.div`
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  background: var(--Shade-White, #fff);
  z-index: 10;

  /* Shadow/md */
  box-shadow: 0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1);
  padding: 14px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Steps = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 0px;
  gap: 4px;
  width: 100%;
  height: 72px;
  margin-left: 10px;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    margin-left: 0;
  }
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  gap: 8px;
  width: 95px;
  cursor: pointer;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    gap: 8px;
    width: 120px;
  }
`;

const LogoIconStyled = styled(LogoIcon)`
  width: 64px;
  flex-shrink: 0;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    width: 128px;
    flex-shrink: 0;
  }
`;

interface StepProps {
  active?: boolean;
}

const StepText = styled.div<StepProps>`
  color: ${props => (props.active ? 'var(--black)' : 'var(--textGray)')};

  /* Text xs/Semibold */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: ${props => (props.active ? 600 : 400)};
  line-height: 18px; /* 150% */
  display: none;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    display: block;
  }
`;

const MobileStepText = styled(StepText)`
  display: block;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    display: none;
  }
`;

const Rectangle = styled.div<StepProps>`
  background-color: ${props => (props.active ? 'var(--green)' : 'var(--lightGreen)')};
  border-radius: 100px;
  flex: none;
  flex-grow: 0;
  height: 3px;
  width: 100%;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    height: 4px;
  }
`;

const Flex = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    max-width: 720px;
  }
`;
